import { defineStore } from "pinia";
import type { CellBoundingBox, CellGroupsPayload } from "@/types/api";
import { isMarkedCell, matchesCellIdentity, moveCellsBetweenArrays } from "@/utils/store";

export const useCellGroupStore = defineStore("cellGroups", {
  state: () => ({
    groupDict: {} as Record<string, CellBoundingBox[]>,
    urlDict: {} as Record<number | string, string>,
  }),
  getters: {
    cellBackendRequestObj: (state): CellGroupsPayload => ({ cells: state.groupDict }),
  },
  actions: {
    setCellGroups(groupDict: Record<string, CellBoundingBox[]>) {
      this.groupDict = groupDict;
    },
    changeCellGroup({
      fromType,
      tileId,
      imageUrl,
      toType,
    }: {
      fromType: string;
      tileId: number;
      imageUrl?: string;
      toType: string;
    }) {
      if (fromType === toType) return;
      const sourceCellArray = this.groupDict[fromType] || [];
      const targetCellArray = this.groupDict[toType] || [];

      const sourceCellIndex = sourceCellArray.findIndex(
        (cell) => matchesCellIdentity(cell, tileId, imageUrl),
      );
      const cell = sourceCellIndex >= 0 ? sourceCellArray[sourceCellIndex] : undefined;
      if (!cell) return;

      const cellsToMove = isMarkedCell(cell) ? sourceCellArray.filter(isMarkedCell) : [cell];
      if (!cellsToMove.length) return;

      const { nextSourceCellArray, nextTargetCellArray } = moveCellsBetweenArrays(
        cellsToMove,
        sourceCellArray,
        targetCellArray
      );

      this.groupDict = {
        ...this.groupDict,
        [fromType]: nextSourceCellArray,
        [toType]: nextTargetCellArray,
      } as Record<string, CellBoundingBox[]>;
    },
    changeBbox({
      fromType,
      tileId,
      imageUrl,
      bboxes,
    }: {
      fromType: string;
      tileId: number;
      imageUrl?: string;
      bboxes: number[][];
    }) {
      const cellArray = this.groupDict[fromType] || [];
      const cellIndex = cellArray.findIndex(
        (cell) => matchesCellIdentity(cell, tileId, imageUrl),
      );
      if (cellIndex < 0) return;
      const newCellArray = cellArray.slice();
      newCellArray[cellIndex] = { ...newCellArray[cellIndex], bounding_boxes: bboxes } as CellBoundingBox;
      this.groupDict = { ...this.groupDict, [fromType]: newCellArray } as Record<
        string,
        CellBoundingBox[]
      >;
    },
    resetGroups() {
      this.groupDict = {} as Record<string, CellBoundingBox[]>;
      this.urlDict = {} as Record<number | string, string>;
    },
    tagCell({
      fromType,
      tileId,
      imageUrl,
      tag,
    }: {
      fromType: string;
      tileId: number;
      imageUrl?: string;
      tag: string;
    }) {
      // Ensure reactivity by replacing the object
      const cellArray = this.groupDict[fromType] || [];
      const cellIndex = cellArray.findIndex(
        (cell) => matchesCellIdentity(cell, tileId, imageUrl),
      );
      if (cellIndex < 0) return;
      // create a shallow copy of the array and replace the modified entry
      const newCellArray = cellArray.slice();
      newCellArray[cellIndex] = { ...newCellArray[cellIndex], tag } as CellBoundingBox;
      this.groupDict = { ...this.groupDict, [fromType]: newCellArray } as Record<
        string,
        CellBoundingBox[]
      >;
    },
    setupCellGroups({
      groupDict,
      allCellTypes,
    }: {
      groupDict: Record<string, CellBoundingBox[]>;
      allCellTypes?: string[];
    }) {
      const tempGroupDict = { ...groupDict } as Record<string, CellBoundingBox[]>;
      (allCellTypes || []).forEach((cellType) => {
        if (!Array.isArray(tempGroupDict[cellType])) {
          tempGroupDict[cellType] = [];
        }
      });
      this.setCellGroups(tempGroupDict);
    },
  },
});

export default useCellGroupStore;
